import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RequestReviewSheet } from "./RequestReviewSheet";
import {
  AccreditationRequest,
  JournalistOption,
  RequestStatus,
  TERM_BADGE_CLASSES,
  TERM_LABELS,
} from "./types";

const STATUS_CONFIG: Record<
  RequestStatus,
  { label: string; className: string }
> = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-800 border-amber-300" },
  docs_requested: {
    label: "Docs Requested",
    className: "bg-blue-100 text-blue-800 border-blue-300",
  },
  under_review: {
    label: "Under Review",
    className: "bg-[#0D2240] text-white border-transparent",
  },
  approved: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border-green-300",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border-red-300",
  },
};

const DOC_KEYS = ["national_id", "press_card", "assignment_letter", "photograph"] as const;

interface PendingRequestsTabProps {
  orgId: string;
  journalists: JournalistOption[];
  onRequestPassForJournalist: (journalistId: string) => void;
}

export function PendingRequestsTab({
  orgId,
  journalists,
  onRequestPassForJournalist,
}: PendingRequestsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reviewRequest, setReviewRequest] = useState<AccreditationRequest | null>(
    null
  );
  const [newRequestPopoverOpen, setNewRequestPopoverOpen] = useState(false);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["accreditation_requests", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("accreditation_requests" as any)
        .select(`
          *,
          journalist:journalist_id (first_name, last_name, email)
        `)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as any[]).map((r) => ({
        ...r,
        outlet_name:
          journalists.find((j) => j.id === r.journalist_id)?.outlet_name ?? null,
      })) as AccreditationRequest[];
    },
    enabled: !!orgId,
  });

  async function createRequest(journalistId: string) {
    setCreatingFor(journalistId);
    try {
      const { error } = await supabase
        .from("accreditation_requests" as any)
        .insert({
          org_id: orgId,
          journalist_id: journalistId,
          status: "pending",
          document_flags: {
            national_id: false,
            press_card: false,
            assignment_letter: false,
            photograph: false,
          },
        });
      if (error) throw error;
      toast({ title: "Accreditation request created" });
      queryClient.invalidateQueries({
        queryKey: ["accreditation_requests", orgId],
      });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setCreatingFor(null);
      setNewRequestPopoverOpen(false);
    }
  }

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["accreditation_requests", orgId] });
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex justify-between items-center">
        <Popover
          open={newRequestPopoverOpen}
          onOpenChange={setNewRequestPopoverOpen}
        >
          <PopoverTrigger asChild>
            <Button
              style={{ backgroundColor: "#006B3C" }}
              className="text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0">
            <Command>
              <CommandInput placeholder="Search journalist…" />
              <CommandList>
                <CommandEmpty>No journalist found.</CommandEmpty>
                <CommandGroup>
                  {journalists.map((j) => (
                    <CommandItem
                      key={j.id}
                      value={`${j.first_name} ${j.last_name} ${j.outlet_name ?? ""}`}
                      onSelect={() => createRequest(j.id)}
                      disabled={!!creatingFor}
                    >
                      {creatingFor === j.id ? (
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      ) : (
                        <ChevronsUpDown className="mr-2 w-4 h-4 opacity-30" />
                      )}
                      <div>
                        <div className="font-medium">
                          {j.first_name} {j.last_name}
                        </div>
                        {j.outlet_name && (
                          <div className="text-xs text-muted-foreground">
                            {j.outlet_name}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: "#0D2240" }}>
              {["Journalist", "Submitted", "Term", "Status", "Documents", "Actions"].map(
                (h) => (
                  <TableHead
                    key={h}
                    style={{ color: "#F0E4B8" }}
                    className="font-semibold"
                  >
                    {h}
                  </TableHead>
                )
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-12 text-muted-foreground"
                >
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-12 text-muted-foreground"
                >
                  No accreditation requests yet.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((req, idx) => {
                const journalist = req.journalist as any;
                const name = journalist
                  ? `${journalist.first_name} ${journalist.last_name}`
                  : "";
                const statusConf =
                  STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;
                const flags = (req.document_flags as Record<string, boolean>) ?? {};

                return (
                  <TableRow
                    key={req.id}
                    className={idx % 2 === 1 ? "bg-[#F7F5F0]" : ""}
                  >
                    <TableCell>
                      <div className="font-medium">{name}</div>
                      {req.outlet_name && (
                        <div className="text-xs text-muted-foreground">
                          {req.outlet_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(req.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>
                      {req.accreditation_term && (
                        <Badge
                          variant="outline"
                          className={TERM_BADGE_CLASSES[req.accreditation_term]}
                        >
                          {TERM_LABELS[req.accreditation_term]}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={statusConf.className}
                      >
                        {statusConf.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        {DOC_KEYS.map((key) => (
                          <span
                            key={key}
                            title={key.replace(/_/g, " ")}
                            className={`w-3 h-3 rounded-full ${
                              flags[key] ? "bg-green-500" : "bg-red-400"
                            }`}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReviewRequest(req)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <RequestReviewSheet
        request={reviewRequest}
        open={!!reviewRequest}
        onClose={() => setReviewRequest(null)}
        onUpdated={refresh}
        onApproveAndIssuePass={(journalistId) => {
          setReviewRequest(null);
          onRequestPassForJournalist(journalistId);
        }}
      />
    </div>
  );
}
